/*
 * Wishlist Drawer — lifecycle (open/close/focus-trap/escape/overlay/return-focus/scroll-lock)
 * + delegated clicks + count badge sync (header + drawer tab). Quick-add
 * slide-up and remove/undo live in assets/wishlist-drawer-actions.js;
 * region/empty state is owned by assets/wishlist-render.js. ≤ 150 lines.
 * (Cross-drawer tab switching between this drawer and the mini-bag is owned
 * entirely by drawer-coordinator.js; this file no longer bridges the bag tab.)
 * Depends on focus-trap.js + window.BroraWishlist + window.BroraWishlistDrawerActions.
 */
;(function () {
  'use strict';
  /* global createFocusTrap */
  const container = document.querySelector('[data-wishlist-drawer]');
  if (!container) return;
  const panel = container.querySelector('[data-wishlist-drawer-panel]');
  const overlay = container.querySelector('[data-wishlist-drawer-overlay]');
  const announcer = container.querySelector('[data-wishlist-drawer-announcer]');
  const abortController = new AbortController();
  const signal = abortController.signal;
  let lastTrigger = null, trap = null, openState = false;
  const actions = (window.BroraWishlistDrawerActions && window.BroraWishlistDrawerActions.build(container, announcer)) || null;

  function isOpen() { return openState; }
  function isShareOpen() { return container.getAttribute('data-wishlist-drawer-share') === 'open'; }
  function openShare(triggerEl) {
    if (actions && typeof actions.openShare === 'function') actions.openShare(triggerEl);
    else container.setAttribute('data-wishlist-drawer-share', 'open');
  }
  function closeShare() {
    if (actions && typeof actions.closeShare === 'function') actions.closeShare();
    else container.setAttribute('data-wishlist-drawer-share', 'closed');
  }

  function syncTriggers(v) {
    document.querySelectorAll('[data-wishlist-drawer-trigger]').forEach(function (t) {
      if (t.hasAttribute('aria-expanded')) t.setAttribute('aria-expanded', v);
    });
    const header = document.querySelector('[data-header]');
    if (header) header.setAttribute('data-wishlist-drawer-open', v);
    const menuToggle = document.querySelector('[data-menu-toggle]');
    if (menuToggle) menuToggle.setAttribute('data-wishlist-drawer-open', v);
  }

  // Opening the wishlist closes any open sibling header surface (search drawer,
  // mini-bag via their close button; the mobile menu by resetting its
  // data-menu-open state) — full mutual exclusion, no edits to those files.
  function closeOtherDrawers() {
    const search = document.querySelector('[data-search-drawer]');
    if (search && search.getAttribute('data-search-drawer-open') === 'true') {
      const c = search.querySelector('[data-search-drawer-close]'); if (c) c.click();
    }
    const header = document.querySelector('[data-header]');
    if (header && header.getAttribute('data-mini-bag-open') === 'true') {
      const c = document.querySelector('[data-mini-bag-close]'); if (c) c.click();
    }
    if (header && header.getAttribute('data-menu-open') === 'true') {
      header.setAttribute('data-menu-open', 'false');
      const mt = header.querySelector('[data-menu-toggle]');
      if (mt) { mt.setAttribute('aria-expanded', 'false'); mt.setAttribute('aria-label', 'Open menu'); }
    }
  }

  // Apply a state change to the panel without animating its translate transition
  // (used by the tab-switch path so the panel jumps on/off-screen instantly while
  // only the CONTENT cross-fades, per Figma 1451:25035). JS-only — no CSS edit.
  function withNoSlide(fn) {
    var prev = panel.style.transition;
    panel.style.transition = 'none';
    fn();
    void panel.offsetWidth; // force reflow so the no-transition write commits
    requestAnimationFrame(function () { panel.style.transition = prev; });
  }

  function openDrawer(trigger, opts) {
    if (isOpen()) return;
    openState = true;
    lastTrigger = trigger || null;
    panel.removeAttribute('aria-hidden'); panel.removeAttribute('inert');
    syncTriggers('true');
    if (opts && opts.tabSwitch) {
      // Panel jumps on-screen with NO slide; content fades in via the host CSS.
      withNoSlide(function () { container.setAttribute('data-wishlist-drawer-open', 'true'); });
    } else {
      closeOtherDrawers();
      container.setAttribute('data-wishlist-drawer-open', 'true');
      const sc = container.querySelector('[data-drawer-scroll]'); if (sc) sc.scrollTop = 0; // open from the top
      document.documentElement.setAttribute('data-scroll-lock', '');
      if (typeof createFocusTrap === 'function') {
        trap = trap || createFocusTrap(panel); trap.activate(); trap.focusFirst();
      }
      // menu-drawer.js removes data-scroll-lock asynchronously (its MutationObserver
      // on data-menu-open) when closeOtherDrawers resets the menu — re-assert the
      // lock on the next microtask (runs after that observer) so the wishlist keeps it.
      queueMicrotask(function () { if (isOpen()) document.documentElement.setAttribute('data-scroll-lock', ''); });
    }
  }

  function closeDrawer(opts) {
    if (!isOpen()) return;
    openState = false;
    panel.setAttribute('aria-hidden', 'true'); panel.setAttribute('inert', '');
    syncTriggers('false');
    if (opts && opts.tabSwitch) {
      // Panel jumps off-screen with NO slide; content has already faded out via host CSS.
      withNoSlide(function () { container.setAttribute('data-wishlist-drawer-open', 'false'); });
    } else {
      container.setAttribute('data-wishlist-drawer-open', 'false');
      document.documentElement.removeAttribute('data-scroll-lock');
      if (actions) actions.closeQuickAdd();
      if (isShareOpen()) closeShare();
      if (trap) trap.deactivate();
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    }
    lastTrigger = null;
  }

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-wishlist-drawer-trigger]');
    if (trigger) { e.preventDefault(); if (!isOpen()) openDrawer(trigger); return; }  // own-icon re-click is a no-op while open (close via the X / overlay)
    // Opening another header drawer (search / mini-bag) closes the wishlist — no
    // preventDefault, so that drawer's own trigger handler still opens it.
    if (isOpen() && e.target.closest('[data-search-drawer-trigger], [data-mini-bag-trigger]')) { closeDrawer(); return; }
    if (e.target.closest('[data-wishlist-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }
    const shareTriggerBtn = e.target.closest('[data-wishlist-drawer-share-trigger]');
    if (shareTriggerBtn && isOpen()) {
      e.preventDefault();
      openShare(shareTriggerBtn);
      return;
    }
    if (!actions) return;
    if (e.target.closest('[data-wishlist-drawer-quick-add-overlay]')) { actions.closeQuickAdd(); return; }
    const moveBtn = e.target.closest('[data-wishlist-drawer-move-to-bag]');
    if (moveBtn) { e.preventDefault(); actions.openQuickAdd(moveBtn.closest('[data-wishlist-drawer-card]')); return; }
    const removeBtn = e.target.closest('[data-wishlist-drawer-remove]');
    if (removeBtn) { e.preventDefault(); actions.toggleRemoved(removeBtn.closest('[data-wishlist-drawer-card]'), true); return; }
    const undoBtn = e.target.closest('[data-wishlist-drawer-undo]');
    if (undoBtn) { e.preventDefault(); actions.toggleRemoved(undoBtn.closest('[data-wishlist-drawer-card]'), false); return; }
    const clearBtn = e.target.closest('[data-wishlist-drawer-clear]');
    if (clearBtn) { e.preventDefault(); actions.clearAll(); return; }
    const clearUndoBtn = e.target.closest('[data-wishlist-drawer-clear-undo]');
    if (clearUndoBtn) { e.preventDefault(); actions.clearAllUndo(); }
  }, { signal });

  // Quick-add size pick — mirrors the mini-bag upsell: radios fire `change`,
  // not a click on the label (see assets/mini-bag.js).
  container.addEventListener('change', function (e) {
    if (actions && e.target.name === 'wishlist-quick-add-size' && e.target.value) actions.pickSize(e.target.value);
  }, { signal });

  document.addEventListener('click', function (e) {
    if (!isOpen()) return;
    const menuBtn = e.target.closest('[data-menu-toggle]');
    if (!menuBtn) return;
    e.preventDefault(); e.stopImmediatePropagation(); closeDrawer();
  }, { signal, capture: true });

  if (overlay) overlay.addEventListener('click', function () {
    if (!isOpen()) return;
    if (isShareOpen()) { closeShare(); return; }
    closeDrawer();
  }, { signal });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !isOpen()) return;
    if (isShareOpen()) { closeShare(); return; }
    closeDrawer();
  }, { signal });

  // Count badge sync — header [data-wishlist-count] + drawer tab
  // [data-wishlist-drawer-count]. Driven by brora:wishlist:changed from
  // wishlist-client.js, then a one-shot hydrate so the badge reflects the
  // saved list on first load (retry-polls until the seam is ready).
  function renderCount(n) {
    const v = n > 0 ? String(n) : '0';
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = v;
      el.classList.toggle('hidden', n === 0);
    });
    document.querySelectorAll('[data-wishlist-drawer-count]').forEach(function (el) {
      el.textContent = v;
    });
  }
  document.addEventListener('brora:wishlist:changed', function (e) {
    renderCount(((e && e.detail && e.detail.items) || []).length);
  }, { signal });
  (function hydrateCount() {
    if (window.BroraWishlist && window.BroraWishlist.getItems) {
      window.BroraWishlist.getItems().catch(function () {});
    } else {
      setTimeout(hydrateCount, 200);
    }
  })();

  window.broraWishlistDrawer = { open: openDrawer, close: closeDrawer, isOpen: isOpen };

  document.addEventListener('shopify:section:unload', function () {
    if (actions) actions.teardown();
    abortController.abort();
  }, { once: true });
})();
